// Angular + external
import {
  ApplicationRef,
  Component,
  ElementRef,
  Injector,
  NgZone,
  ViewEncapsulation,
  Input,
  booleanAttribute,
} from "@angular/core";

// BFX
import * as CubiqTS from "shared/cubiq-model-multi-asset";
import { BfxBaseBlotter, provideBlotter } from "../bfx-base-blotter/bfx-base-blotter";

import { BfxRiskTradeBlotterService } from "./services/bfx-risk-trade-blotter.service";
import { RiskTradeTableDefinitionFactory, ProfileSettingKeys } from "shared/cubiq-services";
import { GoldenLayoutService } from "../golden-layout/golden-layout-service";
import { TranslateService } from "@ngx-translate/core";
import { BfxRiskTradeContextMenuService } from "./services/bfx-risk-trade-contextmenu.service";

import {
  BfxBlotterAction,
  SystemConfigPromiseService,
  ProfilePromiseActionService,
} from "shared/cubiq-services";
import { BfxBlotterFilterService } from "../bfx-blotter-filter/bfx-blotter-filter.service";
import { BfxBaseBlotterColumnSelectorService } from "../bfx-base-blotter/bfx-base-blotter-columnselector.service";

import { Subject, ReplaySubject, Subscription } from "rxjs";
import { BfxRiskTradeFilterService } from "./services/bfx-risk-trade-filter.service";
import { BfxMouseModifierKeyService } from "app/bfx-common/services/bfx-mouse-modifier-key.service";
import { BfxUserProfileService } from "../bfx-common/services/bfx-user-profile.service";
import * as BfxTradesHelper from "app/bfx-common/helpers/trades/BfxTradesHelper";

const BLOTTER_PREFERENCES_KEY = ProfileSettingKeys.userProfileSettingsOrderBlotterPreferences;

import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

import { BfxSaveProfileNotificationService } from "../bfx-common/services/bfx-save-profile-notification.service";
import { GLComponentAlertStatus } from "../golden-layout/golden-layout-component";
import { DealOrderTypeRenderer } from "../bfx-blotter-trade/renderers/DealOrderTypeRenderer";

import { BfxVwapTradeService } from "app/bfx-vwap/bfx-vwap-trade.service";
import { BfxVwapInfo } from "app/bfx-vwap/bfx-vwap-info";
import { BfxBaseVwapBlotter } from "app/bfx-vwap/bfx-base-vwap-blotter";
import { BfxVwapService } from "app/bfx-vwap/bfx-vwap.service";

import {
  TradeOrderFilterType,
  TradeTypeFilterHelper,
} from "app/bfx-interface-controls/helpers/TradeTypeFilterHelper";
import { RowNode } from "ag-grid-community";
import { CommonIntentRenderer } from "app/bfx-blotter-orders/renderers/CommonIntentRenderer";
import { COLUMN_DIR } from "shared/cubiq-services/model/TradeTableDefinitionFactory.constants";

@Component({
  selector: "bfx-risk-trade-blotter",
  templateUrl: "./html/bfx-risk-trade-blotter.html",
  encapsulation: ViewEncapsulation.None,
  providers: [provideBlotter(BfxRiskTradeBlotterComponent)],
  viewProviders: [BfxBaseBlotterColumnSelectorService, BfxRiskTradeBlotterService],
  standalone: false,
})
export class BfxRiskTradeBlotterComponent extends BfxBaseVwapBlotter {
  protected static inMemoryReady: Subject<void> = new ReplaySubject<any>();
  protected static initialized: boolean = false;
  protected static primed: boolean = false;
  protected static refreshSubject: Subject<any> = new Subject<any>();
  protected static queuedRowData: Array<any> = [];
  protected static rawData: any = {};
  protected static refreshColumns: any = {};
  protected static transaction: any = {
    add: [],
    remove: [],
    update: [],
    pending: {},
    lastUpdate: 0,
  };

  private lastItemCounter: number = -1;
  private viewActive: boolean = false;
  private newAlert: boolean = true;

  @Input()
  public embeddedTableModeSearchToggle = false;

  @Input()
  public doubleClickOpenTradeDetail = false;

  @Input({ transform: booleanAttribute }) enableToolbar = true;

  private _tradeTypeFilter: TradeOrderFilterType = TradeOrderFilterType.ALL;

  private subscriptions: Subscription[] = [];
  private rendererSettings: { intentStylingEnabled: boolean };

  public frameworkComponents: any = {
    orderTypeRenderer: DealOrderTypeRenderer,
    directionRenderer: CommonIntentRenderer,
  };

  constructor(
    BfxBlotterElement: ElementRef,
    applicationRef: ApplicationRef,
    injector: Injector,
    public service: BfxRiskTradeBlotterService,
    _contextMenuService: BfxRiskTradeContextMenuService,
    zone: NgZone,
    _tableDefinitionFactory: RiskTradeTableDefinitionFactory,
    layoutService: GoldenLayoutService,
    translationService: TranslateService,
    blotterFilterService: BfxBlotterFilterService,
    filteringService: BfxRiskTradeFilterService,
    columnSelectorService: BfxBaseBlotterColumnSelectorService,
    profileActionService: ProfilePromiseActionService,
    sysConfigService: SystemConfigPromiseService,
    mouseModifierKeyService: BfxMouseModifierKeyService,
    saveProfileNotificationService: BfxSaveProfileNotificationService,
    protected userProfileService: BfxUserProfileService,
    protected vwapTradeService: BfxVwapTradeService,
    protected vwapInfo: BfxVwapInfo,
  ) {
    super(
      BfxBlotterElement,
      applicationRef,
      injector,
      service,
      _contextMenuService,
      zone,
      _tableDefinitionFactory,
      layoutService,
      translationService,
      blotterFilterService,
      filteringService,
      columnSelectorService,
      profileActionService,
      sysConfigService,
      mouseModifierKeyService,
      saveProfileNotificationService,
      userProfileService,
      vwapInfo,
    );

    this.profileActionService.getProfileConfig(BLOTTER_PREFERENCES_KEY).then((profileValue) => {
      this.mapUserSettings(profileValue);
    });
    this.subscriptions.push(
      this.profileActionService
        .getProfileConfigUpdates(BLOTTER_PREFERENCES_KEY)
        .subscribe((profileValue) => {
          this.mapUserSettings(profileValue);
        }),
    );
  }

  /**
   * Setter for trade type so we can drive filter changes
   */
  public set tradeTypeFilter(value: TradeOrderFilterType) {
    logger.out.info("riskt-c - refreshing trade type filter");
    this._tradeTypeFilter = value;
    if (this.gridOptions.api) {
      this.gridOptions.api.onFilterChanged();
      this.afterFilteringCompleted();
    }
  }
  public get tradeTypeFilter(): TradeOrderFilterType {
    return this._tradeTypeFilter;
  }

  getVWapService(): BfxVwapService {
    return this.vwapTradeService;
  }

  getVwapSource(): string {
    return "Risk Trade Blotter";
  }

  getVWapUnits() {
    return "trades";
  }

  public override ngOnDestroy() {
    super.ngOnDestroy();
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
  }

  /**
   * Extract the styling settings and add to the grid context
   * @param profileValue
   */
  private mapUserSettings(profileValue: any) {
    try {
      let savedSettings = JSON.parse(profileValue);
      if (savedSettings != undefined && savedSettings != null) {
        const intentStylingEnabled = savedSettings.intentStylingEnabled === "y";
        if (intentStylingEnabled != this.rendererSettings.intentStylingEnabled) {
          this.rendererSettings.intentStylingEnabled = intentStylingEnabled;
          this.refreshRendererSettings();
        }
      }
    } catch (e) {
      logger.out.warn(`rtb-c - settings error with value: 'profileValue' : ${e}`);
    }
  }

  /**
   * Refresh renderers that refernce changed context data
   */
  private refreshRendererSettings() {
    setTimeout(() => {
      logger.out.info("rtb-c - Refreshing intent col");
      this.gridOptions.api.refreshCells({
        columns: [COLUMN_DIR],
        force: true,
      });
    }, 100);
  }

  /**
   * Override to add in the contextual settings values for renderers
   * @returns
   */
  protected override initGridOptionsContext(): any {
    let retContext = super.initGridOptionsContext();
    // Add a blotterSettings - this is called from constructor so do init here rather than as property initialiser
    this.rendererSettings = { intentStylingEnabled: false };
    retContext.rendererSettings = this.rendererSettings;
    return retContext;
  }

  protected initializeColumnDefinitions(): void {
    super.initializeColumnDefinitions();
    if (!this.embeddedTableMode) {
      this.gridOptions.rowClassRules = BfxTradesHelper.getRiskTradeRowStyles();
    } else if (this.columnDefs && this.columnDefs.length > 0) {
      this.columnDefs[0].valueGetter = null;
    }
  }

  public gridDataUpdated() {
    if (this.gridOptions.api) {
      if (this.lastItemCounter !== this.getBadgeNumber()) {
        this.lastItemCounter = this.getBadgeNumber();
        let alertStatus = this.getAlertStatus();
        this.layoutService.updateBadge(this, this.lastItemCounter, alertStatus);
      }
    }

    super.gridDataUpdated();
  }

  protected getBadgeNumber(): number {
    return this.service.getItemCount();
  }

  protected addListenersToTable(): void {}

  public getBlotterType(): CubiqTS.BlotterType {
    return CubiqTS.BlotterType.RISKTRADE;
  }

  public static getRawOrderData(): Array<any> {
    return BfxRiskTradeBlotterComponent.queuedRowData;
  }

  public static getInMemoryReady(): Subject<void> {
    return BfxRiskTradeBlotterComponent.inMemoryReady;
  }

  public onCellDoubleClicked(event) {
    if (
      event === Object(event) &&
      event.node === Object(event.node) &&
      typeof event.node.id === "string"
    ) {
      if (this.doubleClickOpenTradeDetail)
        this.contextMenuService.fireActionEvent(
          BfxBlotterAction.ACTION_TRADE_VIEW_TRADE_DETAILS,
          this,
          [event.node.id],
          [event.node.data],
          null,
          null,
          null,
          event,
        );
      else
        this.contextMenuService.fireActionEvent(
          BfxBlotterAction.ACTION_TRADE_VIEW_DETAILS,
          this,
          [event.node.id],
          [event.node.data],
          null,
          null,
          null,
          event,
        );
    }
  }
  public showSettings(event: Event): void {
    //   this.layoutService.showSettings("Trade", event);
  }

  public componentActivated(): void {
    this.viewActive = true;
    this.newAlert = false;
  }

  public componentDeactivated(): void {
    if (this.viewActive) {
      // If it was active, then ensure that new alerts are disabled for what we currently show
      this.viewActive = false;
      this.newAlert = false;
    }
  }

  // make it public to add tab flashing functionality
  private getAlertStatus(): GLComponentAlertStatus {
    if (this.getBadgeNumber() == 0 || this.viewActive) {
      return GLComponentAlertStatus.NONE;
    }
    if (!this.viewActive && this.newAlert) {
      return GLComponentAlertStatus.ALERT_FLASH;
    }
    return GLComponentAlertStatus.ALERT;
  }

  public override isExternalFilterPresent(): boolean {
    return this._tradeTypeFilter !== TradeOrderFilterType.ALL || super.isExternalFilterPresent();
  }

  public override doesExternalFilterPass(node: RowNode): boolean {
    if (this._tradeTypeFilter !== TradeOrderFilterType.ALL) {
      if (!TradeTypeFilterHelper.doesFilterPass(this._tradeTypeFilter, node.data)) {
        return false;
      }
    }
    return super.doesExternalFilterPass(node);
  }

  public restoreFromConfiguration(config: any): void {
    this._tradeTypeFilter = config?.tradeTypeFilter ?? TradeOrderFilterType.ALL;
    super.restoreFromConfiguration(config);
  }

  public getComponentConfiguration(): any {
    let config = super.getComponentConfiguration();
    if (config != null) {
      config.tradeTypeFilter = this._tradeTypeFilter;
    }
    return config;
  }

  public getTabIdentifier(): string {
    return "BfxRiskBlotterComponent";
  }
}
